'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { Button } from '@workspace/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@workspace/ui/components/dialog';
import { cn } from '@workspace/ui/lib';
import { Copy, LoaderCircle } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { SystemBankDataItem, SystemBankDetails } from '@/types/bank';

interface BankDetailsDialogProps {
  children: React.ReactNode;
  systemBankDetails?: SystemBankDetails;
  referenceNumber?: string;
  isLoading?: boolean;
  error?: Error | null;
}

interface BankDetailsRow {
  title: string;
  value: string;
}

const REFERENCE_NUMBER_TITLE = 'Reference number';

export function BankDetailsDialog({
  children,
  systemBankDetails,
  referenceNumber,
  isLoading = false,
  error = null,
}: BankDetailsDialogProps) {
  const [open, setOpen] = useState(false);
  const prevSystemBankDetails = useRef<SystemBankDetails | undefined>(undefined);

  // Close dialog when currency changes (systemBankDetails updates)
  useEffect(() => {
    if (
      open &&
      !isLoading &&
      prevSystemBankDetails.current !== systemBankDetails &&
      systemBankDetails
    ) {
      setOpen(false);
    }
    prevSystemBankDetails.current = systemBankDetails;
  }, [systemBankDetails, isLoading, open]);

  // Rows come from the API as-is so per-client overrides (e.g. account + routing number) render too
  const bankDetailsRows = useMemo((): BankDetailsRow[] | null => {
    const dataItems: SystemBankDataItem[] | undefined = systemBankDetails?.data?.[0];
    if (!Array.isArray(dataItems) || dataItems.length === 0) {
      return null;
    }

    const rows = dataItems.map((item) =>
      item.title === REFERENCE_NUMBER_TITLE ? { ...item, value: referenceNumber ?? '' } : item
    );
    if (!rows.some((item) => item.title === REFERENCE_NUMBER_TITLE)) {
      rows.push({ title: REFERENCE_NUMBER_TITLE, value: referenceNumber ?? '' });
    }

    return rows.map((item) => ({
      title: item.title,
      value: item.value === '' || item.value === undefined ? 'Not available' : String(item.value),
    }));
  }, [systemBankDetails, referenceNumber]);

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  const copyAll = async () => {
    if (!bankDetailsRows) return;

    const allDetails = bankDetailsRows.map((row) => `${row.title}: ${row.value}`).join('\n');

    try {
      await navigator.clipboard.writeText(allDetails);
      toast.success('All bank details copied to clipboard');
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle className="text-grey-1000 text-2xl font-bold dark:text-neutral-100">
            Bank details
          </DialogTitle>
        </DialogHeader>

        <div className="flex w-full flex-col space-y-6">
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <LoaderCircle className="text-grey-400 h-6 w-6 animate-spin" />
              <span className="text-grey-600 dark:text-grey-400 ml-2">Loading bank details...</span>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex items-center justify-center py-8">
              <span className="text-red-600 dark:text-red-400">
                Failed to load bank details. Please try again.
              </span>
            </div>
          )}

          {/* Bank Details */}
          {bankDetailsRows && !isLoading && !error && (
            <>
              {bankDetailsRows.map((row, index) => (
                <div
                  key={row.title}
                  className={cn(
                    'flex w-full flex-row items-center justify-between',
                    index > 0 && 'border-grey-200 dark:border-grey-800 border-t pt-6'
                  )}
                >
                  <div className="flex w-full flex-col">
                    <label className="text-grey-600 dark:text-grey-400 text-sm">{row.title}</label>
                    <div className="flex items-center justify-between">
                      <span className="font-normal text-neutral-200 dark:text-neutral-100">
                        {row.value}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="primary-icon"
                    size="base"
                    onClick={() => copyToClipboard(row.value, row.title)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              ))}

              {/* Copy All Button */}
              <Button onClick={copyAll} className="mt-2 w-full" variant="secondary">
                Copy all
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
